import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	Layout,
	ReferenceBadge,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { CarSettings, Checkmark, Close } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import styled from 'styled-components';
import { OfertasContext } from '../../context/ofertas-context';
import {
	financeRequest,
	requestProducts,
} from '../../helpers/calculatorFunctions';
import { checkActiveDisclaimer } from '../../helpers/get-disclaimer';
import {
	capitalizeSentence,
	formatPrecio,
	getConsumptionText,
	getDatosEconomicos,
	getDetalleFinanciacion,
	getEtiquetaDgt,
	getFinanciacionPeriodicidad,
	getFinanciacionRedirection,
	getFinanciacionType,
	getPagoFinanciado,
	getPagoUnico,
} from '../../helpers/ofertas-helper';
import {
	CarlineWithOfertas,
	DetalleFinanciacionType,
	Equipamiento,
	FinanceData,
	FinanciacionType,
	Oferta,
} from '../../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { RedirectCTAs } from '../redirect-CTAs';
import { Gradient } from './content-layer';
import { ImageSlider } from './image-slider';

const StickyBottomContainer = styled.div<{ gradientType?: Gradient }>`
	background-image: ${(props) => props.gradientType};
	bottom: 0px;
	position: sticky;

	@media screen and (max-width: 960px) {
		text-align: center;
	}

	@media screen and (min-width: 1280px) and (max-width: 1599px) {
		background-color: none;
	}

	@media screen and (min-width: 1280px) {
		z-index: -10;
	}
`;

const DesktopContainer = styled.div`
	position: relative;
`;

const SliderContainer = styled.div`
	@media screen and (max-width: 959px) {
		padding-top: ${(props) => props.theme.size.dynamic0100};
	}

	@media screen and (min-width: 1280px) {
		position: absolute;
		top: 0;
		right: 0;
		padding-top: 0;
		width: 50%;
	}
`;

const CloseDiv = styled.div`
	text-align: end;
	cursor: pointer;
`;

const ScrollContainer = styled.div`
	width: 100%;
	max-height: calc(100vh);
	overflow-y: auto;

	::-webkit-scrollbar {
		display: none; /* for Chrome, Safari and Opera */
		scrollbar-width: none; /* Firefox */
	}
`;

// Not in use at the moment (nov.24)
const PersonalizaCTA = ({
	selectedOferta,
}: {
	selectedOferta: Oferta;
}): React.ReactElement => {
	const trackingManager = useTrackingManager();

	const handleOnClick = () => {
		const pagoFinanciado = getPagoFinanciado(selectedOferta.datosEconomicos);
		const pagoUnico = getPagoUnico(selectedOferta.datosEconomicos);
		trackingManager.trackOfertasLinkClick(
			'Personaliza el tuyo',
			{ contentId: 'Personaliza el tuyo' },
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.configuracion.motorizacion.code?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: pagoUnico?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);
	};

	return (
		<Container horizontalAlign={'center'} verticalAlign={'center'}>
			<Text textAlign={TextAlignment.center}>
				<CTA
					tag="a"
					emphasis={'tertiary'}
					icon={<CarSettings />}
					size="large"
					iconPosition="start"
					stretchContent
					onClick={handleOnClick}
				>
					Personaliza el tuyo
				</CTA>
			</Text>
		</Container>
	);
};

const ScrollToSectionButtons = ({
	equipamientoRef,
	precioRef,
	galeriaRef,
}: {
	equipamientoRef: React.RefObject<HTMLElement>;
	precioRef: React.RefObject<HTMLElement>;
	galeriaRef: React.RefObject<HTMLElement>;
}): React.ReactElement => {
	const [selectedSection, setSelectedSection] =
		useState<string>('equipamiento');

	const handleSectionOnClick = (
		sectionName: string,
		sectionRef: React.RefObject<HTMLElement>,
	) => {
		setSelectedSection(sectionName);

		if (sectionRef.current) {
			sectionRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
	};

	return (
		<Container
			data-testid="scroll-to-section__buttons"
			wrap={'never'}
			gutter={ContainerGutter.static250}
			padding={{
				top: ContainerPadding.static400,
			}}
			stretchContent={{
				[Breakpoints.default]: true,
				[Breakpoints.b560]: false,
			}}
		>
			<CTA
				tag="a"
				emphasis={selectedSection === 'equipamiento' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('equipamiento', equipamientoRef)}
			>
				Equipamiento
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'precio' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('precio', precioRef)}
			>
				Precio
			</CTA>
			<CTA
				tag="a"
				emphasis={selectedSection === 'galeria' ? 'nbab' : 'secondary'}
				size="small"
				onClick={() => handleSectionOnClick('galeria', galeriaRef)}
			>
				Galería
			</CTA>
		</Container>
	);
};

export const OfertaLayer = ({
	setIsLayerVisible,
	activeCarline,
	selectedOferta,
	layerRef,
	setIsFinanceLayerVisible,
	setFinanceData,
	isCallMeNowOpen,
}: {
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	activeCarline: CarlineWithOfertas;
	layerRef?: React.RefObject<LayerHandleV2<any>>;
	selectedOferta: Oferta;
	className: string;
	setIsFinanceLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setFinanceData: React.Dispatch<React.SetStateAction<FinanceData>>;
	isCallMeNowOpen: boolean;
}): React.ReactElement => {
	const {
		ofertasService,
		calculatorResult,
		disclaimerManager,
		serviceConfigProvider,
	} = React.useContext(OfertasContext);

	const equipamientoRef = useRef<HTMLElement>(null);
	const precioRef = useRef<HTMLElement>(null);
	const galeriaRef = useRef<HTMLElement>(null);
	const trackingManager = useTrackingManager();
	const [ofertaDataCarlineArray, setOfertaDataCarlineArray] = useState<any>();
	const subscriptionRef = React.useRef<Subscription | null>(null);

	const pagoUnico = getPagoUnico(selectedOferta.datosEconomicos);
	const pagoFinanciado = getPagoFinanciado(selectedOferta.datosEconomicos);

	const selectedDatosEconomicos = getDatosEconomicos(
		selectedOferta.datosEconomicos,
	);

	const activateShorty =
		(
			serviceConfigProvider.configs['es-vwdata']?.customConfig as {
				brand?: string;
			}
		)?.brand === 'V' &&
		selectedOferta.configuracion.motorizacion.combustible === 'Eléctrico';

	useEffect(() => {
		const subscription = ofertasService
			.getDataCalculator(selectedOferta.configuracion.marketingCode)
			.subscribe((dataCarlineArray) => {
				setOfertaDataCarlineArray(dataCarlineArray);
			});
		return () => subscription.unsubscribe();
	}, [ofertasService, selectedOferta.configuracion.marketingCode]);

	useEffect(() => {
		trackingManager.trackOfertasLayerLoad(
			{ contentId: 'Oferta Layer' },
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.configuracion.motorizacion.code?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: pagoUnico?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);
	}, []);

	const handleCloseOnClick = () => {
		setIsLayerVisible(false);

		trackingManager.trackOfertasLayerClose(
			'Close Oferta Layer',
			{
				contentId: 'Close Oferta Layer',
			},
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.configuracion.motorizacion.code?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: pagoUnico?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);

		if (layerRef?.current) {
			layerRef?.current.close();
			return ((layerRef.current as unknown) = null);
		}
	};

	const handleClickFinancialProduct = () => {
		trackingManager.trackOfertasLinkClick(
			'Click financial product',
			{
				contentId: `Oferta Layer - ${pagoFinanciado ? getFinanciacionType(pagoFinanciado) : ''}`,
			},
			{
				CarlineId: selectedOferta.carline?.code.toString(),
				CarlineName: selectedOferta.carline?.title,
				ModelId: selectedOferta.configuracion.motorizacion.code?.split('-')[0],
				ModelName: selectedOferta.carline?.salesgroup.model.name,
				ModelYear: selectedOferta.carline?.salesgroup.year.toString(),
				SalesgroupName: selectedOferta.carline?.salesgroup.title,
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: pagoUnico?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			},
		);
	};

	const handleOnClickFinanceCalc = (calculateSelectedOferta: Oferta) => {
		if (calculateSelectedOferta) {
			setFinanceData({
				selectedOferta: calculateSelectedOferta,
				request: undefined,
			});
			setIsFinanceLayerVisible(true);
			subscriptionRef.current?.unsubscribe();

			if (ofertaDataCarlineArray && calculatorResult?.status === 'ready') {
				const { calculator } = calculatorResult;

				calculator
					.getProductsAsync(
						requestProducts(ofertaDataCarlineArray, calculateSelectedOferta),
					)
					.then((result) => {
						setFinanceData({
							request: financeRequest(
								ofertaDataCarlineArray,
								calculateSelectedOferta,
								result.products.default,
							),
							selectedOferta: calculateSelectedOferta,
							dataFromOfertasLayer: true,
						});
					})
					.catch((error) => {
						console.log(error);
					});

				setIsLayerVisible(false);
				if (layerRef?.current) {
					layerRef?.current.close();
					(layerRef.current as unknown) = null;
				}
				subscriptionRef.current?.unsubscribe();
			}
		}
	};

	const InfoConsumoEmisiones = () => {
		return (
			<Container
				padding={{
					left: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b960]: ContainerPadding.static0,
					},
					right: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b1600]: ContainerPadding.static0,
					},
				}}
				gutter={ContainerGutter.static200}
			>
				<Text appearance={TokenTextAppearance.copy100}>
					{getConsumptionText(
						ofertaDataCarlineArray.configurations.content.consolidatedData
							.wltp_data.interpolations,
						selectedOferta.configuracion.motorizacion.combustible,
					)}
				</Text>
				<Text appearance={TokenTextAppearance.copy100}>
					{`1. La información relativa al consumo de combustible, las emisiones y la autonomía eléctrica ha sido determinada en conformidad a los procedimientos de medición contemplados por la normativa. Dichos datos no hacen referencia a un vehículo específico y no constituyen parte de la oferta. El equipamiento opcional puede afectar a valores relevantes de los vehículos, como el peso, la resistencia y la aerodinámica. Estos factores, junto con la climatología, las condiciones del tráfico y la forma de conducción, pueden afectar el consumo de combustible, el consumo de electricidad, las emisiones CO2 y las prestaciones del vehículo. Para más información sobre los valores oficiales de consumo de combustible y emisiones CO2 en los nuevos vehículos, puedes consultar la ‘Guía de Vehículos Turismo de venta en España, con indicación de consumos y emisiones de CO2’, que está disponible en todos los concesionarios y en la web del Instituto para la Diversificación y Ahorro de la Energía IDAE - El consumo de combustible, las emisiones de CO2 de un vehículo y la autonomía eléctrica , así como las discrepancias en los valores indicados, no dependen únicamente del uso eficaz del combustible por parte del vehículo y de otros factores técnicos (como las ruedas y los neumáticos utilizados), también dependen del tipo de conducción y de otros factores no técnicos. Los valores de emisiones de CO2 pueden variar en función del equipamiento seleccionado, implicando un cambio en el impuesto de matriculación.`}
				</Text>
			</Container>
		);
	};

	return (
		<>
			<DesktopContainer className="desktop-container">
				<Layout
					data-testid="oferta-layer__layout-info"
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 23 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b560]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 22 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b960]: [
							{ name: '.', columns: 2 },
							{ name: 'a', columns: 21 },
							{ name: '.', columns: 1 },
							{ name: '.', columns: 0 },
							{ name: 'b', columns: 0 },
						],
						[Breakpoints.b1280]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 12 },
						],
						[Breakpoints.b1600]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 13 },
						],
						[Breakpoints.b2560]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 10 },
							{ name: '.', columns: 13 },
						],
					}}
					allowOverflowingContent
				>
					<ScrollContainer className="scroll-container">
						<Container
							wrap={'always'}
							horizontalAlign={{ [Breakpoints.b1280]: 'space-between' }}
							padding={{
								top: ContainerPadding.dynamic0100,
								left: {
									[Breakpoints.default]: ContainerPadding.grid001,
									[Breakpoints.b560]: ContainerPadding.static0,
								},
							}}
							shrinkContent
						>
							{/* Titulo, descripcion y boton de cerrado en la layer */}
							<Container
								data-testid="oferta-layer__header header"
								wrap={'never'}
								horizontalAlign={'space-between'}
								verticalAlign={'flex-start'}
								shrinkContent
							>
								<Container data-testid="header__name-etiqueta" wrap={'always'}>
									<Container
										wrap={'never'}
										gutter={ContainerGutter.static200}
										shrinkContent
									>
										<Text
											tag={TextTag.h1}
											appearance={TokenTextAppearance.headline300}
											bold
										>
											{activeCarline.nuevo ? 'Nuevo ' : null}
											{selectedOferta.carline?.salesgroup.title}
										</Text>
										{selectedOferta.configuracion.etiquetaDGT && (
											<Text>
												<img
													src={getEtiquetaDgt(
														selectedOferta.configuracion.etiquetaDGT,
													)}
													width={32}
													alt={activeCarline.title}
												/>
											</Text>
										)}
									</Container>

									<Container shrinkContent>
										<BreakpointWrapper
											min={Breakpoints.default}
											max={Breakpoints.b1920}
										>
											<Text
												tag={TextTag.h3}
												appearance={TokenTextAppearance.copy100}
												bold
											>
												{selectedOferta.configuracion.motorizacion.name}
											</Text>
										</BreakpointWrapper>
										<BreakpointWrapper min={Breakpoints.b1920}>
											<Text
												tag={TextTag.h3}
												appearance={TokenTextAppearance.copy200}
												bold
											>
												{selectedOferta.configuracion.motorizacion.name}
											</Text>
										</BreakpointWrapper>
									</Container>
								</Container>

								<BreakpointWrapper min={Breakpoints.default}>
									<Text>
										<CloseDiv
											className="close-button__container"
											onClick={handleCloseOnClick}
										>
											<Text textAlign={TextAlignment.end}>
												<Close />
											</Text>
										</CloseDiv>
									</Text>
								</BreakpointWrapper>
							</Container>
							{/* Botones de scroll para mobile */}
							<BreakpointWrapper
								min={Breakpoints.default}
								max={Breakpoints.b960}
							>
								<ScrollToSectionButtons
									equipamientoRef={equipamientoRef}
									precioRef={precioRef}
									galeriaRef={galeriaRef}
								/>
							</BreakpointWrapper>
							{/* Informacion del equipamiento destacado */}
							{selectedOferta.configuracion.equipamientos?.serie.length !=
								0 && (
								<Container
									data-testid="oferta-layer__info info"
									padding={{ top: ContainerPadding.static400 }}
									wrap={'always'}
									ref={equipamientoRef}
								>
									<Text
										tag={TextTag.h4}
										appearance={TokenTextAppearance.copy200}
										bold
									>
										Equipamiento destacado
									</Text>
									<Container
										data-testid="info_check-list"
										padding={{ top: ContainerPadding.static250 }}
										wrap={'always'}
									>
										{selectedOferta.configuracion.equipamientos?.serie
											.slice(0, 7)
											.map((equipamiento: Equipamiento, index) => {
												return (
													<Container
														padding={{ bottom: ContainerPadding.static100 }}
														wrap={'never'}
														verticalAlign={'flex-start'}
														gutter={ContainerGutter.static200}
														shrinkContent
														key={index}
													>
														<Text tag={TextTag.span}>
															<Checkmark variant="small" />
														</Text>
														<BreakpointWrapper
															min={Breakpoints.default}
															max={Breakpoints.b1920}
														>
															<Text
																tag={TextTag.span}
																hyphens="auto"
																appearance={TokenTextAppearance.copy100}
															>
																{equipamiento.equipamiento.titulo}
															</Text>
														</BreakpointWrapper>
														<BreakpointWrapper min={Breakpoints.b1920}>
															<Text
																tag={TextTag.span}
																hyphens="auto"
																appearance={TokenTextAppearance.copy200}
															>
																{equipamiento.equipamiento.titulo}
															</Text>
														</BreakpointWrapper>
													</Container>
												);
											})}
									</Container>
								</Container>
							)}

							{/* Precio del vehiculo */}
							<Container
								data-testid="oferta-layer__price price"
								padding={{
									top: ContainerPadding.static400,
									bottom: ContainerPadding.static400,
								}}
								wrap={'always'}
								shrinkContent
								ref={precioRef}
							>
								<Text
									tag={TextTag.h4}
									appearance={TokenTextAppearance.copy200}
									bold
								>
									Elige la opción que encaja contigo:
								</Text>

								<Container
									data-testid="price__info"
									wrap={'never'}
									padding={{
										top: ContainerPadding.static250,
									}}
									shrinkContent
								>
									{selectedDatosEconomicos.map((datoEconomico, index) => {
										return datoEconomico ? (
											<Container wrap={'never'}>
												{/* Divider que solo saldra fuera de mobile y si hay un segundo dato economico */}
												{index === 1 && selectedDatosEconomicos.length > 1 && (
													<BreakpointWrapper min={Breakpoints.b560}>
														<Divider isVertical />
													</BreakpointWrapper>
												)}
												<Container
													gutter={ContainerGutter.static100}
													wrap={'always'}
													padding={{
														right:
															index === 0
																? ContainerPadding.static300
																: undefined,
														left:
															index === 1
																? ContainerPadding.static300
																: undefined,
													}}
													{...(index === 0 && { stretchContent: true })}
													{...(index === 1 && { shrinkContent: true })}
												>
													{/* DINAMIZADO EL PRECIO QUE APARECE */}
													<Container wrap={'auto'}>
														<Text
															tag={TextTag.span}
															appearance={TokenTextAppearance.bigcopy100}
															color={TokenTextColor.active}
															bold
														>
															{/* Modalidad solo aplicada al pago unico */}
															{datoEconomico.periodicidad == 'pago-unico' && (
																<Text
																	appearance={TokenTextAppearance.copy100}
																	bold={false}
																>
																	{capitalizeSentence(datoEconomico.modalidad) +
																		'\u00A0'}
																</Text>
															)}

															{/* Precio con el legal asociado */}
															{datoEconomico.periodicidad == 'pago-unico'
																? formatPrecio(datoEconomico.precio) + '€'
																: parseFloat(datoEconomico?.precio.toFixed(2))
																		.toString()
																		.replace('.', ',') +
																	getFinanciacionPeriodicidad(
																		selectedOferta.datosEconomicos,
																	)}

															<Text sup bold>
																{datoEconomico.legal && (
																	<ReferenceBadge>
																		{checkActiveDisclaimer(
																			disclaimerManager
																				.getScopedDisclaimerManager()
																				.getFooterDisclaimers().reference,
																			datoEconomico.legal,
																		)?.references?.[0].replace('.', '')}
																	</ReferenceBadge>
																)}
															</Text>

															{'\u00A0'}
														</Text>

														{/* Tipo de financiacion con enlace asociado */}
														<Text
															tag={TextTag.span}
															appearance={TokenTextAppearance.bigcopy100}
															color={TokenTextColor.active}
															bold
														>
															{datoEconomico.periodicidad != 'pago-unico' && (
																<>
																	{getFinanciacionRedirection(
																		datoEconomico,
																		(
																			serviceConfigProvider.configs['es-vwdata']
																				?.customConfig as { brand?: string }
																		)?.brand,
																	) ? (
																		<CTA
																			tag={'a'}
																			emphasis={'tertiary'}
																			href={getFinanciacionRedirection(
																				datoEconomico,
																				(
																					serviceConfigProvider.configs[
																						'es-vwdata'
																					]?.customConfig as { brand?: string }
																				)?.brand,
																			)}
																			target={'_blank'}
																			onClick={handleClickFinancialProduct}
																		>
																			{'con\u00A0' +
																				getFinanciacionType(datoEconomico)}
																		</CTA>
																	) : (
																		<>
																			{'con\u00A0' +
																				getFinanciacionType(datoEconomico)}
																		</>
																	)}
																	{'\u00A0'}
																</>
															)}
														</Text>
													</Container>

													{/* Info financiacion */}
													<Container>
														{datoEconomico.periodicidad == 'pago-unico' ? (
															<Text
																tag={TextTag.span}
																appearance={TokenTextAppearance.copy100}
															>
																Sujeto a financiación lineal
															</Text>
														) : (
															<Container wrap={'always'} shrinkContent>
																<Text
																	tag={TextTag.span}
																	appearance={TokenTextAppearance.copy100}
																>
																	{getDetalleFinanciacion(
																		datoEconomico,
																		DetalleFinanciacionType.CUOTAS,
																	)}
																</Text>

																<Text
																	tag={TextTag.span}
																	appearance={TokenTextAppearance.copy100}
																>
																	{getDetalleFinanciacion(
																		datoEconomico,
																		DetalleFinanciacionType.ENTRADA,
																	)}
																</Text>
																{datoEconomico.tipoFinanciacion !=
																	FinanciacionType.MY_RENTING && (
																	<Text
																		tag={TextTag.span}
																		appearance={TokenTextAppearance.copy100}
																	>
																		{getDetalleFinanciacion(
																			datoEconomico,
																			DetalleFinanciacionType.CUOTA_FINAL,
																		)}
																	</Text>
																)}
															</Container>
														)}
													</Container>
												</Container>
											</Container>
										) : null;
									})}
								</Container>

								<BreakpointWrapper
									min={Breakpoints.b960}
									max={Breakpoints.b1280}
								>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
										gutter={
											(
												serviceConfigProvider.configs['es-vwdata']
													?.customConfig as {
													brand?: string;
												}
											)?.brand == 'N'
												? ContainerGutter.dynamic0120
												: ContainerGutter.static0
										}
									>
										<RedirectCTAs
											copy={
												selectedOferta.calculatedFinance.rawResponse
													? 'Pide esta oferta'
													: 'Pide presupuesto'
											}
											emphasis="secondary"
											isMobile={true}
											oferta={selectedOferta}
											formTrigger={
												activateShorty ? 'shorty' : 'es-oneform-lead-embedded'
											}
											trackingRef={'layer'}
										/>

										{(
											serviceConfigProvider.configs['es-vwdata']
												?.customConfig as {
												brand?: string;
											}
										)?.brand == 'N' &&
											isCallMeNowOpen && (
												<RedirectCTAs
													copy={'¿Te llamamos?'}
													emphasis="primary"
													isMobile={true}
													oferta={selectedOferta}
													formTrigger="es-oneform-callme-embedded"
													trackingRef={'layer'}
												/>
											)}
									</Container>
									{ofertaDataCarlineArray?.configurations?.content
										?.consolidatedData?.wltp_data && <InfoConsumoEmisiones />}
								</BreakpointWrapper>
								<BreakpointWrapper min={Breakpoints.b1280}>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.static400,
										}}
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
										gutter={
											(
												serviceConfigProvider.configs['es-vwdata']
													?.customConfig as {
													brand?: string;
												}
											)?.brand == 'N'
												? ContainerGutter.dynamic0120
												: ContainerGutter.static0
										}
									>
										<RedirectCTAs
											copy={
												selectedOferta.calculatedFinance.rawResponse
													? 'Pide esta oferta'
													: 'Pide presupuesto'
											}
											emphasis="secondary"
											isMobile={false}
											oferta={selectedOferta}
											formTrigger={
												activateShorty ? 'shorty' : 'es-oneform-lead-embedded'
											}
											trackingRef={'layer'}
										/>

										{(
											serviceConfigProvider.configs['es-vwdata']
												?.customConfig as {
												brand?: string;
											}
										)?.brand == 'N' &&
											isCallMeNowOpen && (
												<RedirectCTAs
													copy={'¿Te llamamos?'}
													emphasis="primary"
													isMobile={false}
													oferta={selectedOferta}
													formTrigger="es-oneform-callme-embedded"
													trackingRef={'layer'}
												/>
											)}

										{/* <CTA emphasis="primary" tag="button" size="small">
											Consíguelo online
										</CTA> */}
									</Container>
									{ofertaDataCarlineArray?.configurations?.content
										?.consolidatedData?.wltp_data && <InfoConsumoEmisiones />}
								</BreakpointWrapper>
							</Container>
						</Container>
					</ScrollContainer>

					<Container>
						{selectedOferta.galeria && (
							<BreakpointWrapper min={Breakpoints.b1280}>
								<SliderContainer className="slider-container">
									<ImageSlider oferta={selectedOferta} />
								</SliderContainer>
							</BreakpointWrapper>
						)}
					</Container>
				</Layout>

				{selectedOferta.galeria && (
					<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b1280}>
						<Layout
							data-testid="oferta-layer__layout-slider"
							appearance={{
								[Breakpoints.default]: [{ name: 'a', columns: 24 }],
							}}
							allowOverflowingContent
						>
							<SliderContainer className="slider-container" ref={galeriaRef}>
								<ImageSlider oferta={selectedOferta} />
							</SliderContainer>
						</Layout>
					</BreakpointWrapper>
				)}
			</DesktopContainer>

			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				<StickyBottomContainer
					className="sticky-bottom-container"
					gradientType={Gradient.LINEAR_GRADIENT_RIGHT}
				>
					<Layout
						data-testid="oferta-layer__layout-cta-mobile"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 20 },
								{ name: '.', columns: 2 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 11 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 2 },
								{ name: 'a', columns: 8 },
								{ name: '.', columns: 14 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 3 },
								{ name: 'a', columns: 6 },
								{ name: '.', columns: 3 },
								{ name: 'b', columns: 12 },
							],
						}}
						allowOverflowingContent
					>
						<Container
							data-testid="cta-container"
							padding={{
								top: ContainerPadding.static250,
								bottom: ContainerPadding.dynamic0100,
							}}
							verticalAlign={'center'}
							horizontalAlign={{
								[Breakpoints.default]: 'center',
								[Breakpoints.b1280]: 'flex-start',
							}}
							wrap={'always'}
							shrinkContent
							gutter={
								(
									serviceConfigProvider.configs['es-vwdata']?.customConfig as {
										brand?: string;
									}
								)?.brand == 'N'
									? ContainerGutter.static200
									: ContainerGutter.static0
							}
						>
							<RedirectCTAs
								copy={'Pide presupuesto'}
								emphasis="secondary"
								isMobile={true}
								oferta={selectedOferta}
								formTrigger={
									activateShorty ? 'shorty' : 'es-oneform-lead-embedded'
								}
								trackingRef={'layer'}
							/>

							{(
								serviceConfigProvider.configs['es-vwdata']?.customConfig as {
									brand?: string;
								}
							)?.brand == 'N' &&
								isCallMeNowOpen && (
									<RedirectCTAs
										copy={'¿Te llamamos?'}
										emphasis="primary"
										isMobile={true}
										oferta={selectedOferta}
										formTrigger="es-oneform-callme-embedded"
										trackingRef={'layer'}
									/>
								)}

							{/* <CTA
								tag="button"
								emphasis="primary"
								size="large"
								stretchContent={{
									[Breakpoints.default]: true,
									[Breakpoints.b560]: false,
								}}
							>
								Consíguelo online
							</CTA> */}
						</Container>
					</Layout>
				</StickyBottomContainer>
				<Container
					padding={{
						top: ContainerPadding.static250,
						bottom: ContainerPadding.dynamic0100,
					}}
					verticalAlign={'center'}
					horizontalAlign={'center'}
					shrinkContent
				>
					{ofertaDataCarlineArray?.configurations?.content?.consolidatedData
						?.wltp_data && <InfoConsumoEmisiones />}
				</Container>
			</BreakpointWrapper>
		</>
	);
};
